import { CalendarIcon } from 'lucide-react';
import { DisplayFieldConfig } from '@/types/form';
import { getOrderTypeLabel, MoldOrderStatus as MoldOrderStatusType, requireStorageLocation } from '@/types/mold';
import { getArrivalDate, isMaintenanceOrder } from '@/components/utils/moldOrder';
import { formatDate, getShiftLabel, isAllStoreOrProductionType } from '@/utils/dateUtils';
import fetchersRegistry from '@/components/Questionnaire/fetchersRegistry';
import { injectionMachineService, locationStorageService } from '@/services/api';
import { getPriorityLabel, isFinalStep } from '@/components/MoldDetail/utils';
import { Link } from 'react-router-dom';

export const defaultFields: DisplayFieldConfig[] = [
  {
    label: 'ประเภท',
    getValue: (order) => (
      <span className="font-medium">
        {getOrderTypeLabel(order.orderDetail.moldOrderType)}
      </span>
    ),
  },
  {
    label: 'เครื่องที่ผลิต',
    getValue: (order) => (
      <span className="font-medium">{order.orderDetail.machineToProduce ?? '-'}</span>
    ),
    editable: (order) => {
      return [
        MoldOrderStatusType.PENDING_ACKNOWLEDGE,
        MoldOrderStatusType.SUPERVISOR_REVIEW,
        MoldOrderStatusType.REPORTER_CONFIRM,
      ].includes(order.status);
    },
    fieldName: 'machineToProduce',
    editConfig: {
      key: 'machineToProduce',
      label: 'เครื่องที่ผลิต',
      type: 'search_dropdown',
      placeholder: 'ระบุเครื่องที่ผลิต',
      options: [],
      fetch: injectionMachineService.getOptions,
      enforceDropdownSelection: true,
      validationMessage: "กรุณาเลือกตัวเลือกที่ถูกต้องจากรายการ",
      allowInvalidInput: false,
      required: true,
    }
  },
  {
    label: 'สถานที่เก็บหลังล้าง',
    getValue: (order) => {
      const location = order.orderDetail.storageLocation === 'other_custom_text'
        ? order.orderDetail.customStorageLocation
        : order.orderDetail.storageLocation;
      return (
        <span className="font-medium">{location}</span>
      )
    },
    conditionToDisplay: (order) => requireStorageLocation(order.orderDetail.moldOrderType ?? order.type),
    editable: (order) => {
      return [
        MoldOrderStatusType.PENDING_ACKNOWLEDGE,
        MoldOrderStatusType.SUPERVISOR_REVIEW,
        MoldOrderStatusType.REPORTER_CONFIRM,
      ].includes(order.status);
    },
    fieldName: 'storageLocation',
    editConfig: {
      key: 'storageLocation',
      label: 'สถานที่เก็บหลังล้าง',
      type: 'select_with_other',
      fetch: locationStorageService.getAll,
      options: [],
      placeholder: 'ระบุสถานที่เก็บ',
      required: true,
    },
  },
  {
    label: 'ระดับความสำคัญ',
    getValue: (order) => {
      const getPriorityBgColor = (priority: number) => {
        switch (priority) {
          case 1: return 'bg-red-400';
          case 100: return 'bg-red-300';
          default: return 'bg-gray-400';
        }
      }
      const bgColor = getPriorityBgColor(order.priority);
      return (
        <span className={`text-xs font-bold ${bgColor} text-white px-2 py-1 rounded-lg`}>
          {getPriorityLabel(order.priority)}
        </span>
      );
    },
    editable: (order) => {
      return [
        MoldOrderStatusType.PENDING_ACKNOWLEDGE,
      ].includes(order.status);
    },
    fieldName: 'priority',
    editConfig: {
      key: 'priority',
      label: 'ระดับความสำคัญ',
      type: 'select',
      options: [
        {
          value: 200,
          label: 'ปกติ'
        },
        {
          value: 100,
          label: 'เร่งด่วน'
        },
        {
          value: 1,
          label: 'เร่งด่วนมาก'
        }
      ],
      defaultValue: 200,
      required: true,
      entityToUpdate: 'moldOrder',
    },
  },
  {
    label: 'ลูกค้า',
    getValue: (order) => (
      <span className="font-medium">{order.mold.customer.name}</span>
    ),
  },
  {
    label: 'จำนวน SHOT สะสมที่ล้างครั้งนี้',
    getValue: (order) => (
      <span className="font-medium">
        {order.orderDetail.accumulativeShotCount?.toLocaleString() ?? '-'}
      </span>
    ),
    conditionToDisplay: (order) => Object.keys(order.orderDetail).includes('accumulativeShotCount') || isAllStoreOrProductionType(order.orderDetail.moldOrderType),
  },
  {
    label: 'วันที่แจ้ง',
    getValue: (order) => (
      <span className="font-medium text-base">
        {formatDate(new Date(order.createdAt))}
      </span>
    ),
    icon: <CalendarIcon className="h-4 w-4 text-gray-400 mr-2 hidden sm:block" />,
  },
  {
    label: 'กำหนดเสร็จ',
    getValue: (order) => (
      <span className="font-medium text-base">
        {order.orderDetail.dueDate ? formatDate(order.orderDetail.dueDate) : ''}
      </span>
    ),
    icon: <CalendarIcon className="h-4 w-4 text-gray-400 mr-2 hidden sm:block" />,
    editable: (order) => {
      return [
        MoldOrderStatusType.PENDING_ACKNOWLEDGE,
      ].includes(order.status);
    },
    fieldName: 'dueDate',
    editConfig: {
      key: 'dueDate',
      label: 'กำหนดเสร็จ',
      required: true,
      type: 'date',
      placeholder: 'ระบุวันที่',
      minDate: new Date(),
    },
    conditionToDisplay: (order) => Boolean(order.orderDetail.dueDate),
  },
  {
    label: 'กะที่จะผลิต',
    getValue: (order) => (
      <span className="font-medium">{order.orderDetail.shiftCompleted ? getShiftLabel(order.orderDetail.shiftCompleted) : '-'}</span>
    ),
    conditionToDisplay: (order) => Boolean(order.orderDetail.shiftCompleted),
    fieldName: 'shiftCompleted',
    editable: (order) => {
      return [
        MoldOrderStatusType.PENDING_ACKNOWLEDGE,
      ].includes(order.status);
    },
    editConfig: {
      key: 'shiftCompleted',
      label: 'กะที่จะผลิต',
      required: false,
      type: 'select',
      options: [
        {
          value: 'A',
          label: getShiftLabel('A'),
        },
        {
          value: 'B',
          label: getShiftLabel('B'),
        },
      ],
      placeholder: 'ระบุกะที่จะผลิต',
    }
  },
  {
    label: 'สร้างจากใบงาน',
    getValue: (order) => (
      <Link to={`/mold-orders/${order.parentOrderId}`} className="text-blue-500 hover:text-blue-600 underline">
        รหัสใบงาน #{order.parentOrderId}
      </Link>
    ),
    conditionToDisplay: (order) => !!order.parentOrderId && order.parentOrderId !== 0,
  },
  {
    label: 'ข้อควรระวังของแม่พิมพ์',
    getValue: (order) => {
      if (order.mold.remarks) {
        return (<div className="text-red-600 font-bold">
          {order.mold.remarks}
        </div>)
      }
      return <>-</>
    },
    conditionToDisplay: (order) => isMaintenanceOrder(order),
  },
];

export const fieldsForMoldExport: DisplayFieldConfig[] = [
  {
    label: 'ประเภทใบงาน',
    getValue: (order) => (
      <span className="font-medium">
        {getOrderTypeLabel(order.orderDetail.moldOrderType)}
      </span>
    ),
  },
  {
    label: 'ลูกค้า',
    getValue: (order) => (
      <span className="font-medium">{order.mold.customer.name}</span>
    ),
  },
  {
    label: 'สาเหตุที่ส่งออก',
    getValue: (order) => (
      <span className="font-medium">{order.orderDetail.rootCause ?? '-'}</span>
    ),
    editable: (order) => {
      return !isFinalStep(order.status)
    },
    fieldName: 'rootCause',
    editConfig: {
      key: 'rootCause',
      label: 'สาเหตุที่ส่งออก',
      type: 'select_with_other_and_custom',
      options: [],
      fetch: fetchersRegistry['mold.exportRootCausesList'],
      required: true,
    },
  },
  {
    label: 'วันที่แจ้ง',
    getValue: (order) => (
      <span className="font-medium text-base">
        {formatDate(new Date(order.createdAt))}
      </span>
    ),
    icon: <CalendarIcon className="h-4 w-4 text-gray-400 mr-2 hidden sm:block" />,
  },
  {
    label: 'กำหนดการส่งคืน',
    getValue: (order) => (
      <span className="font-medium text-base">
        {order.orderDetail.expectedExportDate ? formatDate(new Date(order.orderDetail.expectedExportDate)) : 'N/A'}
      </span>
    ),
    icon: <CalendarIcon className="h-4 w-4 text-gray-400 mr-2 hidden sm:block" />,
    editable: (order) => {
      return !isFinalStep(order.status);
    },
    fieldName: 'expectedExportDate',
    editConfig: {
      key: 'expectedExportDate',
      label: 'กำหนดการส่งคืน',
      required: false,
      type: 'date',
      placeholder: 'ระบุวันกำหนดการส่งคืน',
      minDate: new Date(),
    },
  },
  {
    label: 'วันส่งออกจริง',
    getValue: (order) => {
      const exportDate = order.orderDetail.exportDate ?? order.orderDetail.form?.moldDate?.exportDate;
      return (
        <span className="font-medium text-base">
          {exportDate ? formatDate(exportDate) : 'N/A'}
        </span>
      )
    },
    icon: <CalendarIcon className="h-4 w-4 text-gray-400 mr-2 hidden sm:block" />,
    fieldName: 'exportDate',
  },
]

export const fieldsForMoldAcceptance: DisplayFieldConfig[] = [
  {
    label: 'ประเภทใบงาน',
    getValue: (order) => (
      <span className="font-medium">
        {getOrderTypeLabel(order.orderDetail.moldOrderType)}
      </span>
    ),
  },
  {
    label: 'ลูกค้า',
    getValue: (order) => (
      <span className="font-medium">{order.mold.customer.name}</span>
    ),
  },
  {
    label: 'วันที่แจ้ง',
    getValue: (order) => (
      <span className="font-medium text-base">
        {formatDate(new Date(order.createdAt))}
      </span>
    ),
    icon: <CalendarIcon className="h-4 w-4 text-gray-400 mr-2 hidden sm:block" />,
  },
  {
    label: 'วันเข้าโรงงาน (คาดการณ์)',
    getValue: (order) => (
      <span className="font-medium text-base">
        {order.orderDetail.expectedArrivalDate ? formatDate(new Date(order.orderDetail.expectedArrivalDate)) : 'N/A'}
      </span>
    ),
    icon: <CalendarIcon className="h-4 w-4 text-gray-400 mr-2 hidden sm:block" />,
    editable: (order) => {
      return [
        MoldOrderStatusType.PENDING_ARRIVAL,
      ].includes(order.status);
    },
    fieldName: 'expectedArrivalDate',
    editConfig: {
      key: 'expectedArrivalDate',
      label: 'วันเข้าโรงงาน (คาดการณ์)',
      required: false,
      type: 'date',
      placeholder: 'ระบุวันเข้าโรงงาน',
      minDate: new Date(),
    },
  },
  {
    label: 'วันเข้าโรงงานจริง',
    getValue: (order) => {
      const arrivalDate = getArrivalDate(order);
      return (
        <span className="font-medium text-base">
          {arrivalDate ? formatDate(arrivalDate) : 'N/A'}
        </span>
      )
    },
    icon: <CalendarIcon className="h-4 w-4 text-gray-400 mr-2 hidden sm:block" />,
    fieldName: 'arrivalDate',
  },
  {
    label: 'ใบงานย่อยที่เกี่ยวข้อง',
    getValue: (order) => {
      if (!order.hasSubOrders || !order.subOrders || order.subOrders.length === 0) {
        return <span className="text-gray-500">ไม่มีใบงานย่อยที่เกี่ยวข้อง</span>;
      }
      return (
        <div className="flex flex-col space-y-1">
          {order.subOrders && order.subOrders?.map(sub => {
            return <Link to={`/mold-orders/${sub.id}`} className="text-blue-500 hover:text-blue-600 underline">
              รหัสใบงาน #{sub.id}
            </Link>
          })}
        </div>
      )
    },
    conditionToDisplay: (order) => !!(order.hasSubOrders && order.subOrders && order.subOrders.length > 0),
  },
]

export const fieldsForRepair: DisplayFieldConfig[] = [
  {
    label: 'ประเภทใบงาน',
    getValue: (order) => (
      <span className="font-medium">
        {getOrderTypeLabel(order.orderDetail.moldOrderType)}
      </span>
    ),
  },
  {
    label: 'ลูกค้า',
    getValue: (order) => (
      <span className="font-medium">{order.mold.customer.name}</span>
    ),
  },
  {
    label: 'สาเหตุที่จะซ่อม',
    getValue: (order) => (
      <span className="font-medium">{order.orderDetail.rootCause}</span>
    ),
    editable: (order) => {
      return [
        MoldOrderStatusType.REPORTER_FILL_FORM,
      ].includes(order.status);
    },
    fieldName: 'rootCause',
    editConfig: {
      key: 'rootCause',
      label: 'สาเหตุที่จะซ่อม',
      type: 'select_with_other_and_custom',
      options: [],
      fetch: fetchersRegistry['repairRootCausesList'],
      required: true,
    },
  },
  {
    label: 'เครื่องที่ทำการผลิต',
    getValue: (order) => (
      <span className="font-medium">{order.orderDetail.machineToProduce ?? '-'}</span>
    ),
    editable: (order) => {
      return [
        MoldOrderStatusType.REPORTER_FILL_FORM,
        MoldOrderStatusType.PENDING_ACKNOWLEDGE
      ].includes(order.status);
    },
    fieldName: 'machineToProduce',
    editConfig: {
      key: 'machineToProduce',
      label: 'เครื่องที่ผลิต',
      type: 'search_dropdown',
      placeholder: 'ระบุเครื่องที่ผลิต',
      options: [],
      fetch: injectionMachineService.getOptions,
      enforceDropdownSelection: true,
      validationMessage: "กรุณาเลือกตัวเลือกที่ถูกต้องจากรายการ",
      allowInvalidInput: false,
      required: true,
    }
  },
  {
    label: 'รายละเอียดที่ต้องการให้ดำเนินการ',
    getValue: (order) => {
      const requirementNote = order.orderDetail.form?.basicInfo?.modifyRequirementNote || order.orderDetail.modifyRequirementNote;
      return (
        <span className="font-medium">{requirementNote}</span>
      )
    },
    editable: (order) => {
      return [
        MoldOrderStatusType.REPORTER_FILL_FORM,
      ].includes(order.status);
    },
    fieldName: 'modifyRequirementNote',
    editConfig: {
      key: 'modifyRequirementNote',
      label: 'รายละเอียดที่ต้องการให้ดำเนินการ',
      type: 'textarea',
      placeholder: 'ระบุรายละเอียดที่ต้องการให้ดำเนินการ',
      required: true,
    },
  },
  {
    label: 'วันที่สร้างใบงาน',
    getValue: (order) => (
      <span className="font-medium text-base">
        {formatDate(new Date(order.createdAt))}
      </span>
    ),
    icon: <CalendarIcon className="h-4 w-4 text-gray-400 mr-2 hidden sm:block" />,
  },
  {
    label: 'กำหนดเสร็จ',
    getValue: (order) => (
      <span className="font-medium text-base">
        {order.orderDetail.dueDate ? formatDate(order.orderDetail.dueDate) : ''}
      </span>
    ),
    icon: <CalendarIcon className="h-4 w-4 text-gray-400 mr-2 hidden sm:block" />,
    editable: (order) => {
      return [
        MoldOrderStatusType.REPORTER_FILL_FORM,
      ].includes(order.status);
    },
    fieldName: 'dueDate',
    editConfig: {
      key: 'dueDate',
      label: 'กำหนดเสร็จ',
      required: true,
      type: 'date',
      placeholder: 'ระบุวันที่',
      minDate: new Date(),
    },
    conditionToDisplay: (order) => Boolean(order.orderDetail.dueDate),
  },
]

export const fieldsForMinorRepair: DisplayFieldConfig[] = [
  {
    label: 'ประเภทใบงาน',
    getValue: (order) => (
      <span className="font-medium">
        {getOrderTypeLabel(order.orderDetail.moldOrderType)}
      </span>
    ),
  },
  {
    label: 'ลูกค้า',
    getValue: (order) => (
      <span className="font-medium">{order.mold.customer.name}</span>
    ),
  },
  {
    label: 'ปัญหาเกิดระหว่าง',
    getValue: (order) => (
      <span className="font-medium">{order.orderDetail.problemDuring ?? ''}</span>
    ),
    editable: (order) => {
      return [
        MoldOrderStatusType.REPORTER_FILL_FORM
      ].includes(order.status);
    },
    fieldName: 'problemDuring',
    editConfig: {
      key: 'problemDuring',
      label: 'ปัญหาเกิดระหว่าง',
      type: 'select',
      placeholder: 'ระบุปัญหาเกิดระหว่าง',
      required: true,
      options: [],
      fetch: fetchersRegistry['repairProblemDuringList'],
    },
  },
  {
    label: 'เครื่องที่ทำการผลิต',
    getValue: (order) => (
      <span className="font-medium">{order.orderDetail.machineToProduce ?? '-'}</span>
    ),
    editable: (order) => {
      return [
        MoldOrderStatusType.REPORTER_FILL_FORM,
        MoldOrderStatusType.PENDING_ACKNOWLEDGE
      ].includes(order.status);
    },
    fieldName: 'machineToProduce',
    editConfig: {
      key: 'machineToProduce',
      label: 'เครื่องที่ผลิต',
      type: 'search_dropdown',
      placeholder: 'ระบุเครื่องที่ผลิต',
      options: [],
      fetch: injectionMachineService.getOptions,
      enforceDropdownSelection: true,
      validationMessage: "กรุณาเลือกตัวเลือกที่ถูกต้องจากรายการ",
      allowInvalidInput: false,
      required: true,
    }
  },
  {
    label: 'วันที่สร้างใบงาน',
    getValue: (order) => (
      <span className="font-medium text-base">
        {formatDate(new Date(order.createdAt))}
      </span>
    ),
    icon: <CalendarIcon className="h-4 w-4 text-gray-400 mr-2 hidden sm:block" />,
  },
  {
    label: 'กำหนดเสร็จ',
    getValue: (order) => (
      <span className="font-medium text-base">
        {order.orderDetail.dueDate ? formatDate(order.orderDetail.dueDate) : ''}
      </span>
    ),
    icon: <CalendarIcon className="h-4 w-4 text-gray-400 mr-2 hidden sm:block" />,
    editable: (order) => {
      return [
        MoldOrderStatusType.REPORTER_FILL_FORM,
      ].includes(order.status);
    },
    fieldName: 'dueDate',
    editConfig: {
      key: 'dueDate',
      label: 'กำหนดเสร็จ',
      required: true,
      type: 'date',
      placeholder: 'ระบุวันที่',
      minDate: new Date(),
    },
    conditionToDisplay: (order) => Boolean(order.orderDetail.dueDate),
  },
]

export const fieldsForModify: DisplayFieldConfig[] = [
  {
    label: 'ประเภทใบงาน',
    getValue: (order) => (
      <span className="font-medium">
        {getOrderTypeLabel(order.orderDetail.moldOrderType)}
      </span>
    ),
  },
  {
    label: 'ระดับความสำคัญ',
    getValue: (order) => {
      const getPriorityBgColor = (priority: number) => {
        switch (priority) {
          case 1: return 'bg-red-400';
          case 100: return 'bg-red-300';
          default: return 'bg-gray-400';
        }
      }
      const bgColor = getPriorityBgColor(order.priority);
      return (
        <span className={`text-xs font-bold ${bgColor} text-white px-2 py-1 rounded-lg`}>
          {getPriorityLabel(order.priority)}
        </span>
      );
    },
    editable: (order) => {
      return [
        MoldOrderStatusType.PENDING_ACKNOWLEDGE,
        MoldOrderStatusType.REPORTER_FILL_FORM,
        MoldOrderStatusType.PICK_UP,
      ].includes(order.status);
    },
    fieldName: 'priority',
    editConfig: {
      key: 'priority',
      label: 'ระดับความสำคัญ',
      type: 'select',
      options: [
        {
          value: 200,
          label: 'ปกติ'
        },
        {
          value: 100,
          label: 'เร่งด่วน'
        },
        {
          value: 1,
          label: 'เร่งด่วนมาก'
        }
      ],
      defaultValue: 200,
      required: true,
      entityToUpdate: 'moldOrder',
    },
  },
  {
    label: 'ลูกค้า',
    getValue: (order) => (
      <span className="font-medium">{order.mold.customer.name}</span>
    ),
  },
  {
    label: 'สาเหตุที่ต้องการแก้ไข',
    getValue: (order) => (
      <span className="font-medium">{order.orderDetail.modifyCause}</span>
    ),
    conditionToDisplay: (order) => Boolean(order.orderDetail.modifyCause && order.orderDetail.modifyCause.length > 0),
  },
  {
    label: 'รายละเอียดที่ต้องการให้ดำเนินการ',
    getValue: (order) => {
      const requirementNote = order.orderDetail.form?.basicInfo?.modifyRequirementNote || order.orderDetail.modifyRequirementNote;
      return (
        <span className="font-medium">{requirementNote}</span>
      )
    },
  },
  {
    label: 'วันที่สร้างใบงาน',
    getValue: (order) => (
      <span className="font-medium text-base">
        {formatDate(new Date(order.createdAt))}
      </span>
    ),
    icon: <CalendarIcon className="h-4 w-4 text-gray-400 mr-2 hidden sm:block" />,
  },
  {
    label: 'กำหนดเสร็จ',
    getValue: (order) => (
      <span className="font-medium text-base">
        {order.orderDetail.dueDate ? formatDate(order.orderDetail.dueDate) : ''}
      </span>
    ),
    icon: <CalendarIcon className="h-4 w-4 text-gray-400 mr-2 hidden sm:block" />,
    editable: (order) => {
      return [
        MoldOrderStatusType.REPORTER_FILL_FORM,
      ].includes(order.status);
    },
    fieldName: 'dueDate',
    editConfig: {
      key: 'dueDate',
      label: 'กำหนดเสร็จ',
      required: true,
      type: 'date',
      placeholder: 'ระบุวันที่',
      minDate: new Date(),
    },
    conditionToDisplay: (order) => Boolean(order.orderDetail.dueDate),
  },
]